import type { EventBus, EventPayloads, Term } from './EventBus';

export interface UnlockRecord {
  term: Term;
  /** Fictional-clock minutes at unlock (e.g. 675 = 11:15 AM). */
  fictionalMinutes: number;
  /** Beat id that triggered the unlock, or null if outside a scripted beat. */
  beatId: string | null;
}

export interface Scorecard {
  dunningSent: number;
  dunningRightBucket: number;
  cashApplicationsSent: number;
  cashAppliedUsd: number;
  cashCorrectCustomer: number;
  shortPaysResolved: number;
  disputesLogged: number;
  unappliedAtCloseUsd: number;
  promiseToPaySolid: number;
  promiseToPaySoft: number;
  writeOffsApproved: number;
  writeOffsDeclined: number;
  dsoAtClose: number | null;
  paymentsExpired: number;
  emailsActionedCorrectly: number;
  emailsActionedWrong: number;
  liveScore: number;
}

export interface SessionSnapshot {
  termUnlocks: ReadonlyMap<Term, UnlockRecord>;
  scorecard: Readonly<Scorecard>;
}

interface Clock {
  readonly fictionalMinutes: number;
}

export interface SessionTrackerOptions {
  bus: EventBus;
  clock: Clock;
}

export class SessionTracker {
  private readonly termUnlocks = new Map<Term, UnlockRecord>();
  private readonly lastBeatByTerm = new Map<Term, string>();
  private readonly scorecard: Scorecard = {
    dunningSent: 0,
    dunningRightBucket: 0,
    cashApplicationsSent: 0,
    cashAppliedUsd: 0,
    cashCorrectCustomer: 0,
    shortPaysResolved: 0,
    disputesLogged: 0,
    unappliedAtCloseUsd: 0,
    promiseToPaySolid: 0,
    promiseToPaySoft: 0,
    writeOffsApproved: 0,
    writeOffsDeclined: 0,
    dsoAtClose: null,
    paymentsExpired: 0,
    emailsActionedCorrectly: 0,
    emailsActionedWrong: 0,
    liveScore: 0,
  };

  private readonly onBeatTriggered = (p: { beatId: string; term: Term }): void => {
    this.lastBeatByTerm.set(p.term, p.beatId);
  };

  private readonly onTermUnlocked = (p: { term: Term }): void => {
    // First encounter wins — modals fire term:unlocked when they open, and
    // the teach panel may also fire it later if the player clicks the help
    // link. We want to preserve the earliest encounter time.
    if (this.termUnlocks.has(p.term)) return;
    this.termUnlocks.set(p.term, {
      term: p.term,
      fictionalMinutes: this.opts.clock.fictionalMinutes,
      beatId: this.lastBeatByTerm.get(p.term) ?? null,
    });
  };

  private readonly onDunningSent = (p: EventPayloads['dunning:sent']): void => {
    this.scorecard.dunningSent += 1;
    if (p.correctForBucket) {
      this.scorecard.dunningRightBucket += 1;
      this.applyScore(5, 'dunning:correct-bucket');
    } else {
      this.applyScore(-2, 'dunning:wrong-bucket');
    }
  };

  private readonly onCashApplicationSent = (p: EventPayloads['cash-application:sent']): void => {
    this.scorecard.cashApplicationsSent += 1;
    this.scorecard.cashAppliedUsd += p.allocations.reduce((sum, a) => sum + a.amount, 0);
    if (p.correctCustomer) this.scorecard.cashCorrectCustomer += 1;
    this.scorecard.unappliedAtCloseUsd += p.unapplied;
    // Live score: +15 for correct customer, -5 for wrong, +5 baseline for engaging.
    this.applyScore(p.correctCustomer ? 20 : 0, p.correctCustomer ? 'cash-app:correct' : 'cash-app:wrong');
  };

  private readonly onPaymentExpired = (p: EventPayloads['payment:expired']): void => {
    this.scorecard.paymentsExpired += 1;
    this.scorecard.unappliedAtCloseUsd += p.payment.amount;
    // Penalty scaled to amount: -2 per $1000 of unapplied wire.
    this.applyScore(-Math.round((p.payment.amount / 1000) * 2), 'payment:expired');
  };

  private readonly onShortPayResolved = (): void => {
    this.scorecard.shortPaysResolved += 1;
  };

  private readonly onDisputeResolved = (): void => {
    this.scorecard.disputesLogged += 1;
  };

  private readonly onPtpRecorded = (p: EventPayloads['ptp:recorded']): void => {
    const hasAmount = p.amount > 0;
    const hasDate = p.date !== null && p.date.length > 0;
    if (hasAmount && hasDate) this.scorecard.promiseToPaySolid += 1;
    else if (hasAmount || hasDate) this.scorecard.promiseToPaySoft += 1;
  };

  private readonly onWriteOffApproved = (): void => {
    this.scorecard.writeOffsApproved += 1;
  };

  private readonly onWriteOffDeclined = (): void => {
    this.scorecard.writeOffsDeclined += 1;
  };

  private readonly onEmailActionTaken = (p: EventPayloads['email:action-taken']): void => {
    if (p.correctness === 'correct') {
      this.scorecard.emailsActionedCorrectly += 1;
      this.applyScore(3, 'email:correct');
    } else if (p.correctness === 'wrong') {
      this.scorecard.emailsActionedWrong += 1;
      this.applyScore(-2, 'email:wrong');
    }
    // 'acceptable' = engaged but not optimal — no score change.
  };

  constructor(private readonly opts: SessionTrackerOptions) {
    this.opts.bus.on('beat:triggered', this.onBeatTriggered);
    this.opts.bus.on('term:unlocked', this.onTermUnlocked);
    this.opts.bus.on('dunning:sent', this.onDunningSent);
    this.opts.bus.on('cash-application:sent', this.onCashApplicationSent);
    this.opts.bus.on('short-pay:resolved', this.onShortPayResolved);
    this.opts.bus.on('dispute:resolved', this.onDisputeResolved);
    this.opts.bus.on('ptp:recorded', this.onPtpRecorded);
    this.opts.bus.on('write-off:approved', this.onWriteOffApproved);
    this.opts.bus.on('write-off:declined', this.onWriteOffDeclined);
    this.opts.bus.on('payment:expired', this.onPaymentExpired);
    this.opts.bus.on('email:action-taken', this.onEmailActionTaken);
  }

  private applyScore(delta: number, reason: string): void {
    if (delta === 0) return;
    this.scorecard.liveScore += delta;
    this.opts.bus.emit('score:changed', { score: this.scorecard.liveScore, delta, reason });
  }

  computeDsoSnapshot(): number {
    const s = this.scorecard;
    const raw = 47
      + (s.unappliedAtCloseUsd / 5000)
      - (s.cashCorrectCustomer * 1.5)
      - (s.shortPaysResolved * 0.8)
      - (s.disputesLogged * 0.5)
      + (s.writeOffsApproved * 0.3);
    const clamped = Math.max(38, Math.min(56, raw));
    const dso = Math.round(clamped);
    this.scorecard.dsoAtClose = dso;
    return dso;
  }

  snapshot(): SessionSnapshot {
    return {
      termUnlocks: new Map(this.termUnlocks),
      scorecard: { ...this.scorecard },
    };
  }

  destroy(): void {
    this.opts.bus.off('beat:triggered', this.onBeatTriggered);
    this.opts.bus.off('term:unlocked', this.onTermUnlocked);
    this.opts.bus.off('dunning:sent', this.onDunningSent);
    this.opts.bus.off('cash-application:sent', this.onCashApplicationSent);
    this.opts.bus.off('short-pay:resolved', this.onShortPayResolved);
    this.opts.bus.off('dispute:resolved', this.onDisputeResolved);
    this.opts.bus.off('ptp:recorded', this.onPtpRecorded);
    this.opts.bus.off('write-off:approved', this.onWriteOffApproved);
    this.opts.bus.off('write-off:declined', this.onWriteOffDeclined);
    this.opts.bus.off('payment:expired', this.onPaymentExpired);
    this.opts.bus.off('email:action-taken', this.onEmailActionTaken);
  }
}
